<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";

  .disabled {
    @include colors.color(textDisabledGrey);
  }

  .link {
    @include colors.color(primary_40);
  }
</style>

<script>
  // Components
  import Dropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/Dropdown.svelte";
  import ListItem from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/ListItem.svelte";
  // Stores
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import { candidateStore, todosModalItemStore } from "../stores";
  import { showToast, hasLicense } from "@sourcewhale/core";
  import { startSearches } from "@sourcewhale/api";
  export let enrichmentText = "";

  let isEnriching = false;
  let firstEnrichmentStarted = false;
  let leadingIcon = "fa-regular fa-magnifying-glass";

  let previousContactCount = 0;
  let newContactCount = 0;
  let messageTimer;
  let closeDropdown;

  const getItemSource = (kind) =>
    kind === "phone" || kind === "email"
      ? "/assets/img/logo/png/icon300.png"
      : `/assets/img/licenses/${kind.split("--")[0]}.png`;

  const getItemText = (kind, title) =>
    kind === "phone" || kind === "email"
      ? `Enrich ${kind}s via SourceWhale`
      : `Enrich via ${title}`;

  // Organize items with priority for email and phone
  const organizeItems = (items) => {
    if (!items) return [];

    const priorityItems = ["email", "phone"]
      .filter((type) => items[type])
      .map((type) => [type, items[type]]);

    const otherItems = Object.entries(items).filter(
      ([kind]) => !priorityItems.some(([type]) => type === kind)
    );

    return [...priorityItems, ...otherItems];
  };

  const handleItemClick = (kind) => {
    startSearches({
      candidateIds: [$todosModalItemStore.candidateId],
      contactTypes: kind === "email" ? ["work", "personal"] : [kind],
      current: true,
    }).then(() => closeDropdown());
  };

  const getContactCount = () =>
    ($candidateStore?.phones?.length || 0) +
    ($candidateStore?.emails?.length || 0);

  const handleEnrichmentStarted = () => {
    previousContactCount = getContactCount();
    firstEnrichmentStarted = true;
    leadingIcon = "Spinner color-primary_50";
    enrichmentText = "Searching for results...";
    messageTimer = setTimeout(() => {
      enrichmentText =
        "We are still searching. Retrieving contact details from your connected data provider is taking longer than expected...";
    }, 30000);
  };

  // firstEnrichmentStarted used to prevent toast from showing on initial load
  const handleEnrichmentFinished = () => {
    if (!firstEnrichmentStarted) return;
    newContactCount = getContactCount();
    const toast =
      newContactCount > previousContactCount
        ? {
            title: "Success!",
            subtitle: "Contact details enriched.",
            type: "success",
          }
        : {
            title: "No search results",
            subtitle: "No new contact details have been found.",
            type: "info",
          };
    showToast(toast);
    clearTimeout(messageTimer);
    leadingIcon = "fa-regular fa-magnifying-glass";
    enrichmentText = "";
  };

  // Reactively set isEnriching based on candidateStore changes
  $: {
    const now = Math.round(Date.now() / 1000);
    isEnriching = $candidateStore?.enrichments?.some((x) => now < x) || false;
  }

  // React to isEnriching changes (must be separate from candidateStore changes)
  $: {
    if (isEnriching) {
      handleEnrichmentStarted();
    } else {
      handleEnrichmentFinished();
    }
  }

  $: getDisabled = (kind) =>
    (["phone", "email"].includes(kind) && !hasLicense($userStore, "data")) ||
    (kind === "phone" && !$userStore?.phoneCredits) ||
    (kind === "email" && !$userStore?.emailCredits);
</script>

<Dropdown
  kind="ghost-blue"
  placement="bottom-start"
  {leadingIcon}
  trailingIcon=""
  dynamicWidth={false}
  bind:close={closeDropdown}
  disabled={$candidateStore.isDeleted}
>
  <span slot="button">Enrich</span>
  {#each organizeItems($userStore?.searchKinds || {}) as [kind, item]}
    {@const disabled = getDisabled(kind)}
    {@const onclick = () => handleItemClick(kind)}
    {@const src = getItemSource(kind)}
    {@const text = getItemText(kind, item?.title)}
    {#if disabled}
      <ListItem {src} {onclick} {disabled}>
        <span class:disabled>{text}</span>
        <span slot="tooltip">
          {#if !hasLicense($userStore, "data")}
            Enriching with SourceWhale requires a data license.
          {:else if $userStore?.isAdmin}
            <a
              class="link"
              href="https://sourcewhale.app/admin#billing"
              target="_blank"
            >
              Purchase credits
            </a>
            via the Admin panel
          {:else}
            Please ask your admin to purchase credits
          {/if}
        </span>
      </ListItem>
    {:else}
      <ListItem {src} {onclick} {disabled}>
        {text}
      </ListItem>
    {/if}
  {/each}
</Dropdown>
